import { useEffect, useState } from "react";
import type { StoryboardProject, StudioAsset, StoryBoard, StoryBriefState, AdPlan, AdInputs, AdBriefState, StoryboardBuild } from "./types";

// Tiny pub/sub between the chat-driven flows (CorridoChatFlow lives inside a
// Director chat bubble) and the always-visible studio chrome (StoryboardTimeline,
// CreditHud). Window events keep the two trees decoupled — the chat console
// stays the single place actions are INITIATED; the studio chrome mirrors and
// sends edit intents back.

const PROJECT_EVENT = "director-studio:project";
const REORDER_EVENT = "director-studio:reorder";
const SELECT_EVENT = "director-studio:select";
const DURATION_EVENT = "director-studio:duration";
const APPROVE_EVENT = "director-studio:approve";
// Board -> flow: regenerate one clip (optionally steered by a scene note), and
// kick off the render once every clip is approved. These moved OUT of the chat
// bubble and onto the board so every edit action lives where the storyboard is
// (user directive 2026-07-21).
const REGEN_EVENT = "director-studio:regen";
const RENDER_EVENT = "director-studio:render";
// Board -> flow: the answer to the price question the board shows after Render.
const RENDER_CONFIRM_EVENT = "director-studio:render-confirm";
const RENDER_CANCEL_EVENT = "director-studio:render-cancel";

// ---- Tool rail -> console: start one of the workflows we actually have ----
// The rail and the console's own greeting cards must run the SAME code path,
// or the rail becomes a second way to start a job that drifts from the first.
// This event IS that shared path.

export type WorkflowId = "corrido" | "tiktok_story" | "ads";

/** The engines that publish progress/results onto the board. */
export type StudioService = "corrido" | "story" | "ads";

const WORKFLOW_EVENT = "director-studio:start-workflow";

export function requestWorkflowStart(id: WorkflowId) {
  window.dispatchEvent(new CustomEvent(WORKFLOW_EVENT, { detail: id }));
}

export function useWorkflowStartEvent(handler: (id: WorkflowId) => void) {
  useEffect(() => {
    const onStart = (event: Event) => handler((event as CustomEvent<WorkflowId>).detail);
    window.addEventListener(WORKFLOW_EVENT, onStart);
    return () => window.removeEventListener(WORKFLOW_EVENT, onStart);
  }, [handler]);
}

let lastProject: StoryboardProject | null = null;

export function publishStoryboardProject(project: StoryboardProject | null) {
  lastProject = project;
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(PROJECT_EVENT, { detail: project }));
}

/** Live view of the current storyboard project (null = no active run). */
export function useStoryboardProject(): StoryboardProject | null {
  const [project, setProject] = useState<StoryboardProject | null>(lastProject);
  useEffect(() => {
    const onProject = (event: Event) =>
      setProject((event as CustomEvent<StoryboardProject | null>).detail);
    window.addEventListener(PROJECT_EVENT, onProject);
    return () => window.removeEventListener(PROJECT_EVENT, onProject);
  }, []);
  return project;
}

// ---- Story brief: chat plans it, the board reviews it, the board approves it ----
// The card is where the user edits and commits; StoryChatFlow owns the API and
// the credit charge and listens for the intents below.

const BRIEF_EVENT = "director-studio:story-brief";
const BRIEF_RENDER_EVENT = "director-studio:story-render";
const BRIEF_REPLAN_EVENT = "director-studio:story-replan";
const BRIEF_DISCARD_EVENT = "director-studio:story-discard";

let lastBrief: StoryBriefState | null = null;

/** Flow -> board: a planned board is ready for review (null clears the card). */
export function publishStoryBrief(brief: StoryBriefState | null) {
  lastBrief = brief;
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(BRIEF_EVENT, { detail: brief }));
}

/** Live view of the board awaiting approval (null = nothing planned). */
export function useStoryBrief(): StoryBriefState | null {
  const [brief, setBrief] = useState<StoryBriefState | null>(lastBrief);
  useEffect(() => {
    const onBrief = (event: Event) =>
      setBrief((event as CustomEvent<StoryBriefState | null>).detail);
    window.addEventListener(BRIEF_EVENT, onBrief);
    return () => window.removeEventListener(BRIEF_EVENT, onBrief);
  }, []);
  return brief;
}

/** Card -> flow: user approved this board (as edited). Charges and renders. */
export function requestStoryRender(board: StoryBoard, music: File | null) {
  window.dispatchEvent(new CustomEvent(BRIEF_RENDER_EVENT, { detail: { board, music } }));
}

/** Card -> flow: user wants the Director to plan the writing again. Free. */
export function requestStoryReplan() {
  window.dispatchEvent(new CustomEvent(BRIEF_REPLAN_EVENT));
}

// Chat quick-actions -> card: nudge one field of the board the user is
// reviewing ("use a female narrator", "make it viral"). Merges into the card's
// LIVE state rather than republishing the brief, so a chip can never wipe
// narration the user has already rewritten.
const BRIEF_PATCH_EVENT = "director-studio:story-patch";

export function requestStoryBoardPatch(patch: Partial<StoryBoard>) {
  window.dispatchEvent(new CustomEvent(BRIEF_PATCH_EVENT, { detail: patch }));
}

export function useStoryBoardPatch(handler: (patch: Partial<StoryBoard>) => void) {
  useEffect(() => {
    const onPatch = (event: Event) => handler((event as CustomEvent<Partial<StoryBoard>>).detail);
    window.addEventListener(BRIEF_PATCH_EVENT, onPatch);
    return () => window.removeEventListener(BRIEF_PATCH_EVENT, onPatch);
  }, [handler]);
}

/** Chat quick-action -> card: commit the board exactly as it stands now. The
 *  card owns the edits and the music file, so it does the actual dispatch. */
const BRIEF_COMMIT_EVENT = "director-studio:story-commit";

export function requestStoryCommit() {
  window.dispatchEvent(new CustomEvent(BRIEF_COMMIT_EVENT));
}

export function useStoryCommit(handler: () => void) {
  useEffect(() => {
    const onCommit = () => handler();
    window.addEventListener(BRIEF_COMMIT_EVENT, onCommit);
    return () => window.removeEventListener(BRIEF_COMMIT_EVENT, onCommit);
  }, [handler]);
}

/** Card -> flow: user rejected this board outright. */
export function requestStoryDiscard() {
  window.dispatchEvent(new CustomEvent(BRIEF_DISCARD_EVENT));
}

export function useStoryBriefEvents(handlers: {
  onRender?: (board: StoryBoard, music: File | null) => void;
  onReplan?: () => void;
  onDiscard?: () => void;
}) {
  useEffect(() => {
    const onRender = (event: Event) => {
      const { board, music } = (event as CustomEvent<{ board: StoryBoard; music: File | null }>).detail;
      handlers.onRender?.(board, music);
    };
    const onReplan = () => handlers.onReplan?.();
    const onDiscard = () => handlers.onDiscard?.();
    window.addEventListener(BRIEF_RENDER_EVENT, onRender);
    window.addEventListener(BRIEF_REPLAN_EVENT, onReplan);
    window.addEventListener(BRIEF_DISCARD_EVENT, onDiscard);
    return () => {
      window.removeEventListener(BRIEF_RENDER_EVENT, onRender);
      window.removeEventListener(BRIEF_REPLAN_EVENT, onReplan);
      window.removeEventListener(BRIEF_DISCARD_EVENT, onDiscard);
    };
  }, [handlers.onRender, handlers.onReplan, handlers.onDiscard]);
}

const ASSETS_EVENT = "director-studio:assets";
let lastAssets: StudioAsset[] = [];

/** Flow -> library: the user attached files (song/photos) through the chat. */
export function publishStudioAssets(assets: StudioAsset[]) {
  lastAssets = assets;
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(ASSETS_EVENT, { detail: assets }));
}

export function useStudioAssets(): StudioAsset[] {
  const [assets, setAssets] = useState<StudioAsset[]>(lastAssets);
  useEffect(() => {
    const onAssets = (event: Event) =>
      setAssets((event as CustomEvent<StudioAsset[]>).detail);
    window.addEventListener(ASSETS_EVENT, onAssets);
    return () => window.removeEventListener(ASSETS_EVENT, onAssets);
  }, []);
  return assets;
}

/** Timeline -> flow: user dragged clips into a new order (array of clip idx). */
export function requestClipReorder(order: number[]) {
  window.dispatchEvent(new CustomEvent(REORDER_EVENT, { detail: order }));
}

/** Timeline -> flow: user clicked a clip. */
export function requestClipSelect(idx: number) {
  window.dispatchEvent(new CustomEvent(SELECT_EVENT, { detail: idx }));
}

/** Timeline -> flow: user changed a clip's target length. */
export function requestClipDuration(idx: number, durationSec: number) {
  window.dispatchEvent(new CustomEvent(DURATION_EVENT, { detail: { idx, durationSec } }));
}

/** Timeline -> flow: user toggled a clip's approval (approve/unapprove). */
export function requestClipApprove(idx: number) {
  window.dispatchEvent(new CustomEvent(APPROVE_EVENT, { detail: idx }));
}

/** Board -> flow: regenerate one clip's still (optional scene note steers it).
 *  `reasons`: the "what's wrong" chips picked (lib/corrido-reasons); they only
 *  teach the tool and ride along only when there are some. */
export function requestClipRegen(idx: number, scene?: string, reasons?: string[]) {
  const detail: { idx: number; scene?: string; reasons?: string[] } = { idx, scene };
  if (reasons && reasons.length) detail.reasons = [...reasons];
  window.dispatchEvent(new CustomEvent(REGEN_EVENT, { detail }));
}

/** Board -> flow: every clip is approved — ask for the render (the flow owns the
 *  credit confirmation and the API call). Returns whether a flow heard it, so
 *  the board can say so instead of sitting there when nothing is listening. */
export function requestBoardRender(): boolean {
  const detail = { heard: false };
  window.dispatchEvent(new CustomEvent(RENDER_EVENT, { detail }));
  return detail.heard;
}

/** Board -> flow: the price shown on the board was confirmed — start the render. */
export function requestBoardRenderConfirm() {
  window.dispatchEvent(new CustomEvent(RENDER_CONFIRM_EVENT));
}

/** Board -> flow: "Keep editing" — take the price question down. */
export function requestBoardRenderCancel() {
  window.dispatchEvent(new CustomEvent(RENDER_CANCEL_EVENT));
}

export function useStudioEditEvents(handlers: {
  onReorder?: (order: number[]) => void;
  onSelect?: (idx: number) => void;
  onDuration?: (idx: number, durationSec: number) => void;
  onApprove?: (idx: number) => void;
  onRegen?: (idx: number, scene?: string, reasons?: string[]) => void;
  onRender?: () => void;
  onRenderConfirm?: () => void;
  onRenderCancel?: () => void;
}) {
  useEffect(() => {
    const onReorder = (event: Event) =>
      handlers.onReorder?.((event as CustomEvent<number[]>).detail);
    const onSelect = (event: Event) =>
      handlers.onSelect?.((event as CustomEvent<number>).detail);
    const onDuration = (event: Event) => {
      const { idx, durationSec } = (event as CustomEvent<{ idx: number; durationSec: number }>).detail;
      handlers.onDuration?.(idx, durationSec);
    };
    const onApprove = (event: Event) =>
      handlers.onApprove?.((event as CustomEvent<number>).detail);
    const onRegen = (event: Event) => {
      const { idx, scene, reasons } = (event as CustomEvent<{ idx: number; scene?: string; reasons?: string[] }>).detail;
      handlers.onRegen?.(idx, scene, Array.isArray(reasons) && reasons.length ? reasons : undefined);
    };
    const onRender = (event: Event) => {
      if (!handlers.onRender) return;
      const detail = (event as CustomEvent<{ heard?: boolean } | null>).detail;
      if (detail) detail.heard = true;
      handlers.onRender();
    };
    const onRenderConfirm = () => handlers.onRenderConfirm?.();
    const onRenderCancel = () => handlers.onRenderCancel?.();
    window.addEventListener(REORDER_EVENT, onReorder);
    window.addEventListener(SELECT_EVENT, onSelect);
    window.addEventListener(DURATION_EVENT, onDuration);
    window.addEventListener(APPROVE_EVENT, onApprove);
    window.addEventListener(REGEN_EVENT, onRegen);
    window.addEventListener(RENDER_EVENT, onRender);
    window.addEventListener(RENDER_CONFIRM_EVENT, onRenderConfirm);
    window.addEventListener(RENDER_CANCEL_EVENT, onRenderCancel);
    return () => {
      window.removeEventListener(REORDER_EVENT, onReorder);
      window.removeEventListener(SELECT_EVENT, onSelect);
      window.removeEventListener(DURATION_EVENT, onDuration);
      window.removeEventListener(APPROVE_EVENT, onApprove);
      window.removeEventListener(REGEN_EVENT, onRegen);
      window.removeEventListener(RENDER_EVENT, onRender);
      window.removeEventListener(RENDER_CONFIRM_EVENT, onRenderConfirm);
      window.removeEventListener(RENDER_CANCEL_EVENT, onRenderCancel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handlers.onReorder, handlers.onSelect, handlers.onDuration, handlers.onApprove, handlers.onRegen, handlers.onRender, handlers.onRenderConfirm, handlers.onRenderCancel]);
}

// ---- Generation ON the canvas — CapCut's blueprint puts the work itself on
// the board: while a job runs, a 376px loading card is a NODE; when it
// finishes, the result is a 376px player NODE. These events feed those nodes.

/** A job in flight, shown as the canvas loading card. */
export interface StudioProgress {
  service: StudioService;
  /** Card title, e.g. "Rendering your corrido". */
  title: string;
  /** Live status line from the pipeline. */
  message: string;
  /** 0-100, or null for an indeterminate phase (planning, uploading). */
  progress: number | null;
  /** The live build numbers (corrido): when present the card draws the same
   *  smooth bar, time left and counts as the chat, instead of a bare %. */
  build?: StoryboardBuild | null;
}

// Corrido and story can be in flight at the same time (each flow lives in its
// own chat bubble), so progress and results are keyed per service — one flow
// finishing must never wipe the other's node off the board.
const PROGRESS_EVENT = "director-studio:progress";
const progressByService = new Map<string, StudioProgress>();

export function publishStudioProgress(service: StudioService, progress: Omit<StudioProgress, "service"> | null) {
  if (progress) progressByService.set(service, { ...progress, service });
  else progressByService.delete(service);
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(PROGRESS_EVENT));
}

export function useStudioProgress(): StudioProgress[] {
  const [progress, setProgress] = useState<StudioProgress[]>([...progressByService.values()]);
  useEffect(() => {
    const onProgress = () => setProgress([...progressByService.values()]);
    window.addEventListener(PROGRESS_EVENT, onProgress);
    return () => window.removeEventListener(PROGRESS_EVENT, onProgress);
  }, []);
  return progress;
}

/** A finished render, shown as the canvas player node. */
export interface StudioResult {
  service: StudioService;
  /** Player title, e.g. "Corrido · my_song.mp3". */
  title: string;
  /** blob: URL or an auth-gated /api/ path (the player resolves either). */
  url: string;
}

const RESULT_EVENT = "director-studio:result";
const resultByService = new Map<string, StudioResult>();

export function publishStudioResult(service: StudioService, result: Omit<StudioResult, "service"> | null) {
  if (result) resultByService.set(service, { ...result, service });
  else resultByService.delete(service);
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(RESULT_EVENT));
}

export function useStudioResult(): StudioResult[] {
  const [result, setResult] = useState<StudioResult[]>([...resultByService.values()]);
  useEffect(() => {
    const onResult = () => setResult([...resultByService.values()]);
    window.addEventListener(RESULT_EVENT, onResult);
    return () => window.removeEventListener(RESULT_EVENT, onResult);
  }, []);
  return result;
}

// ---- STAGE-2 CLIP REVIEW on the board ----
// The animated-clip review (watch each clip, re-roll/replace, approve, assemble)
// used to live only in the chat bubble — so a RESUMED run reopened all its
// options in the chat, exactly what the user said should be on the board. This
// channel mirrors the review onto the board; the board dispatches the actions
// back and CorridoChatFlow (which owns the API calls) executes them.

export interface StudioReviewClip {
  idx: number;
  /** Auth-gated video URL for the animated take. */
  videoUrl: string;
  /** Auth-gated still URL for the strip thumbnail. */
  thumbUrl: string | null;
  kind: string;
  theme?: string | null;
  qc?: { ok: boolean; problems: string[] } | null;
}

export interface StudioClipReview {
  runId: string;
  clips: StudioReviewClip[];
  /** idxs the user has approved for the final video. */
  approved: number[];
  /** idx currently rendering a re-roll/replace, or null. */
  rerolling: number | null;
  /** how many free re-rolls remain. */
  rerollsLeft: number;
  /** selected clip idx (big preview). */
  selectedIdx: number | null;
  /** a job is assembling — the board goes read-only meanwhile. */
  busy: boolean;
  /** The live numbers of the new take being filmed (while `rerolling` is set). */
  build?: StoryboardBuild | null;
}

const REVIEW_EVENT = "director-studio:clip-review";
let lastReview: StudioClipReview | null = null;

/** Flow -> board: publish the current stage-2 review (null clears it). */
export function publishClipReview(review: StudioClipReview | null) {
  lastReview = review;
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(REVIEW_EVENT, { detail: review }));
}

export function useClipReview(): StudioClipReview | null {
  const [review, setReview] = useState<StudioClipReview | null>(lastReview);
  useEffect(() => {
    const onReview = (event: Event) => setReview((event as CustomEvent<StudioClipReview | null>).detail);
    window.addEventListener(REVIEW_EVENT, onReview);
    return () => window.removeEventListener(REVIEW_EVENT, onReview);
  }, []);
  return review;
}

const REVIEW_SELECT = "director-studio:review-select";
const REVIEW_APPROVE = "director-studio:review-approve";
const REVIEW_REROLL = "director-studio:review-reroll";
const REVIEW_REPLACE = "director-studio:review-replace";
const REVIEW_USEALLCLEAN = "director-studio:review-useallclean";
const REVIEW_ASSEMBLE = "director-studio:review-assemble";

export function requestReviewSelect(idx: number) { window.dispatchEvent(new CustomEvent(REVIEW_SELECT, { detail: idx })); }
export function requestReviewApprove(idx: number) { window.dispatchEvent(new CustomEvent(REVIEW_APPROVE, { detail: idx })); }
/** A new take of one clip. `{ idx, reasons }` carries the "what's wrong" chips;
 *  a bare idx (the old call) still works, and the event detail stays a bare
 *  number whenever no reason was picked. */
export function requestReviewReroll(arg: number | { idx: number; reasons?: string[] }) {
  const idx = typeof arg === "number" ? arg : arg.idx;
  const reasons = typeof arg === "number" ? undefined : arg.reasons;
  window.dispatchEvent(new CustomEvent(REVIEW_REROLL, { detail: reasons && reasons.length ? { idx, reasons: [...reasons] } : idx }));
}
/** Replace one clip's scene: `(idx, scene?)` as before, or `{ idx, scene, reasons }`. */
export function requestReviewReplace(arg: number | { idx: number; scene?: string; reasons?: string[] }, scene?: string) {
  const d = typeof arg === "number" ? { idx: arg, scene } : arg;
  const detail: { idx: number; scene?: string; reasons?: string[] } = { idx: d.idx, scene: d.scene };
  if (d.reasons && d.reasons.length) detail.reasons = [...d.reasons];
  window.dispatchEvent(new CustomEvent(REVIEW_REPLACE, { detail }));
}
export function requestReviewUseAllClean() { window.dispatchEvent(new CustomEvent(REVIEW_USEALLCLEAN)); }
export function requestReviewAssemble() { window.dispatchEvent(new CustomEvent(REVIEW_ASSEMBLE)); }

export function useClipReviewEvents(handlers: {
  onSelect?: (idx: number) => void;
  onApprove?: (idx: number) => void;
  onReroll?: (idx: number, reasons?: string[]) => void;
  onReplace?: (idx: number, scene?: string, reasons?: string[]) => void;
  onUseAllClean?: () => void;
  onAssemble?: () => void;
}) {
  useEffect(() => {
    const picked = (r: unknown) => (Array.isArray(r) && r.length ? (r as string[]) : undefined);
    const onSelect = (e: Event) => handlers.onSelect?.((e as CustomEvent<number>).detail);
    const onApprove = (e: Event) => handlers.onApprove?.((e as CustomEvent<number>).detail);
    const onReroll = (e: Event) => {
      const d = (e as CustomEvent<number | { idx: number; reasons?: string[] }>).detail;
      if (typeof d === "number") handlers.onReroll?.(d);
      else if (d && typeof d.idx === "number") handlers.onReroll?.(d.idx, picked(d.reasons));
    };
    const onReplace = (e: Event) => {
      const d = (e as CustomEvent<{ idx: number; scene?: string; reasons?: string[] }>).detail;
      handlers.onReplace?.(d.idx, d.scene, picked(d.reasons));
    };
    const onUseAllClean = () => handlers.onUseAllClean?.();
    const onAssemble = () => handlers.onAssemble?.();
    window.addEventListener(REVIEW_SELECT, onSelect);
    window.addEventListener(REVIEW_APPROVE, onApprove);
    window.addEventListener(REVIEW_REROLL, onReroll);
    window.addEventListener(REVIEW_REPLACE, onReplace);
    window.addEventListener(REVIEW_USEALLCLEAN, onUseAllClean);
    window.addEventListener(REVIEW_ASSEMBLE, onAssemble);
    return () => {
      window.removeEventListener(REVIEW_SELECT, onSelect);
      window.removeEventListener(REVIEW_APPROVE, onApprove);
      window.removeEventListener(REVIEW_REROLL, onReroll);
      window.removeEventListener(REVIEW_REPLACE, onReplace);
      window.removeEventListener(REVIEW_USEALLCLEAN, onUseAllClean);
      window.removeEventListener(REVIEW_ASSEMBLE, onAssemble);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handlers.onSelect, handlers.onApprove, handlers.onReroll, handlers.onReplace, handlers.onUseAllClean, handlers.onAssemble]);
}

// ---- Commercials & Ads brief: chat plans it, the board reviews it, the board
// approves it. Same shape as the story channel: AdsChatFlow owns the API and
// the credit charge; AdBriefCard owns the edits and dispatches the intents.

const AD_BRIEF_EVENT = "director-studio:ad-brief";
const AD_RENDER_EVENT = "director-studio:ad-render";
const AD_REPLAN_EVENT = "director-studio:ad-replan";
const AD_DISCARD_EVENT = "director-studio:ad-discard";
const AD_PATCH_EVENT = "director-studio:ad-patch";
const AD_INPUTS_PATCH_EVENT = "director-studio:ad-inputs-patch";
const AD_COMMIT_EVENT = "director-studio:ad-commit";

let lastAdBrief: AdBriefState | null = null;

/** Flow -> board: a planned ad is ready for review (null clears the card). */
export function publishAdBrief(brief: AdBriefState | null) {
  lastAdBrief = brief;
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(AD_BRIEF_EVENT, { detail: brief }));
}

/** Live view of the ad plan awaiting approval (null = nothing planned). */
export function useAdBrief(): AdBriefState | null {
  const [brief, setBrief] = useState<AdBriefState | null>(lastAdBrief);
  useEffect(() => {
    const onBrief = (event: Event) => setBrief((event as CustomEvent<AdBriefState | null>).detail);
    window.addEventListener(AD_BRIEF_EVENT, onBrief);
    return () => window.removeEventListener(AD_BRIEF_EVENT, onBrief);
  }, []);
  return brief;
}

/** Card -> flow: user approved this plan (as edited) with these inputs (as
 *  toggled on the card). Charges once and submits ONE worker job. */
export function requestAdRender(plan: AdPlan, inputs: AdInputs) {
  window.dispatchEvent(new CustomEvent(AD_RENDER_EVENT, { detail: { plan, inputs } }));
}

/** Card/chat -> flow: plan again (free), optionally steered by a note. */
export function requestAdReplan(note?: string) {
  window.dispatchEvent(new CustomEvent(AD_REPLAN_EVENT, { detail: note ?? "" }));
}

/** Card -> flow: user rejected this plan outright. */
export function requestAdDiscard() {
  window.dispatchEvent(new CustomEvent(AD_DISCARD_EVENT));
}

/** Chat quick-action -> card: nudge one field of the plan the user is
 *  reviewing. Merges into the card's LIVE state so a chip never wipes an edit. */
export function requestAdPlanPatch(patch: Partial<AdPlan>) {
  window.dispatchEvent(new CustomEvent(AD_PATCH_EVENT, { detail: patch }));
}

export function useAdPlanPatch(handler: (patch: Partial<AdPlan>) => void) {
  useEffect(() => {
    const onPatch = (event: Event) => handler((event as CustomEvent<Partial<AdPlan>>).detail);
    window.addEventListener(AD_PATCH_EVENT, onPatch);
    return () => window.removeEventListener(AD_PATCH_EVENT, onPatch);
  }, [handler]);
}

/** Chat quick-action -> card: nudge the INPUTS on the card (narrator, music,
 *  captions) — these ride along with the plan on Generate. */
export function requestAdInputsPatch(patch: Partial<AdInputs>) {
  window.dispatchEvent(new CustomEvent(AD_INPUTS_PATCH_EVENT, { detail: patch }));
}

export function useAdInputsPatch(handler: (patch: Partial<AdInputs>) => void) {
  useEffect(() => {
    const onPatch = (event: Event) => handler((event as CustomEvent<Partial<AdInputs>>).detail);
    window.addEventListener(AD_INPUTS_PATCH_EVENT, onPatch);
    return () => window.removeEventListener(AD_INPUTS_PATCH_EVENT, onPatch);
  }, [handler]);
}

/** Chat quick-action -> card: commit the plan exactly as it stands now. */
export function requestAdCommit() {
  window.dispatchEvent(new CustomEvent(AD_COMMIT_EVENT));
}

export function useAdCommit(handler: () => void) {
  useEffect(() => {
    const onCommit = () => handler();
    window.addEventListener(AD_COMMIT_EVENT, onCommit);
    return () => window.removeEventListener(AD_COMMIT_EVENT, onCommit);
  }, [handler]);
}

export function useAdBriefEvents(handlers: {
  onRender?: (plan: AdPlan, inputs: AdInputs) => void;
  onReplan?: (note: string) => void;
  onDiscard?: () => void;
}) {
  useEffect(() => {
    const onRender = (event: Event) => {
      const { plan, inputs } = (event as CustomEvent<{ plan: AdPlan; inputs: AdInputs }>).detail;
      handlers.onRender?.(plan, inputs);
    };
    const onReplan = (event: Event) => handlers.onReplan?.((event as CustomEvent<string>).detail || "");
    const onDiscard = () => handlers.onDiscard?.();
    window.addEventListener(AD_RENDER_EVENT, onRender);
    window.addEventListener(AD_REPLAN_EVENT, onReplan);
    window.addEventListener(AD_DISCARD_EVENT, onDiscard);
    return () => {
      window.removeEventListener(AD_RENDER_EVENT, onRender);
      window.removeEventListener(AD_REPLAN_EVENT, onReplan);
      window.removeEventListener(AD_DISCARD_EVENT, onDiscard);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handlers.onRender, handlers.onReplan, handlers.onDiscard]);
}

// ---- Stills grid result on the canvas (image ads). A finished stills job is
// a grid NODE with a download link per picture, beside the player node.

export interface StudioStills {
  service: "ads";
  /** Grid title, e.g. "Image ads · 8". */
  title: string;
  /** Public storage URLs — no auth needed. */
  items: Array<{ url: string; aspect: string; index: number }>;
}

const STILLS_EVENT = "director-studio:stills";
let lastStills: StudioStills | null = null;

/** Flow -> board: a finished stills grid (null clears the node). */
export function publishStudioStills(stills: StudioStills | null) {
  lastStills = stills;
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent(STILLS_EVENT, { detail: stills }));
}

export function useStudioStills(): StudioStills | null {
  const [stills, setStills] = useState<StudioStills | null>(lastStills);
  useEffect(() => {
    const onStills = (event: Event) => setStills((event as CustomEvent<StudioStills | null>).detail);
    window.addEventListener(STILLS_EVENT, onStills);
    return () => window.removeEventListener(STILLS_EVENT, onStills);
  }, []);
  return stills;
}
